import axios from "axios";

import { apiUrl } from "./apiUrl";

// Relative to the base URL set in src/index.js
const BASE_URL = "";

// // Get Max Service Number API
export const getMaxServiceNumber = async () => {
  const url = `${BASE_URL}/UserRegister/maxId`;
  
  try {
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching max service number:", error);
    throw error.response
      ? error.response.data
      : { message: "Server Error. Unable to fetch service number." };
  }
};

// Same as getMaxServiceNumber but for T
export const getMaxServiceNumberWithT = async () => {
  const url = `${BASE_URL}/UserRegister/GetUserMaxIDWithT`;

  try {
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching max service number (T):", error);
    throw error.response
      ? error.response.data
      : { message: "Server Error. Unable to fetch T-type service number." };
  }
};

// Registration API
export const registerUser = async (userData) => {
  const url = `${BASE_URL}/UserRegister/PostUserDetails`;
  
  try {
    const response = await axios.post(url, [userData]);
    return response.data;
  } catch (error) {
    console.error("Error registering user:", error);
    throw error.response
      ? error.response.data
      : { message: "Server Error. Unable to register user." };
  }
};

// Combined OTP verification and send API
export const verificationCallWithValid = async (phoneNumber) => {
  const url = `${BASE_URL}/Login/VerificationCallWithValid`;
  
  const cleanNumber = phoneNumber.replace("+", "");
  
  try {
    const response = await axios.post(url, {
      MobileNumber: cleanNumber
    });
    
    console.log("OTP Verification Response:", response.data);
    
    // Extract service number from response
    let serviceNo = null;
    if (response.data?.serviceNo?.resultSet?.[0]?.serviceNo) {
      serviceNo = response.data.serviceNo.resultSet[0].serviceNo;
    } else if (response.data?.serviceNo?.resultSet?.[0]?.serviceno) {
      serviceNo = response.data.serviceNo.resultSet[0].serviceno;
    } else if (response.data?.resultSet?.[0]?.serviceNo) {
      serviceNo = response.data.resultSet[0].serviceNo;
    }
    
    return {
      success: true,
      otp: response.data.otp || response.data.VerificationCode,
      serviceNo: serviceNo,
      rawResponse: response.data
    };
  } catch (error) {
    console.error("Error sending verification:", error);
    throw error.response
      ? error.response.data
      : { message: "Server Error. Unable to send verification." };
  }
};

// Service Number Login API
export const serviceNumberLogin = async (serviceNo, password) => {
  const url = `${BASE_URL}/Login/UserLogin`;
  
  try {
    const response = await axios.post(url, null, {
      params: {
        serviceNo: serviceNo,
        password: password
      }
    });
    
    console.log("Service Login Response:", response.data);
    
    // Check if login was successful
    if (response.data.StatusCode === 200) {
      return {
        success: true,
        data: response.data,
        authKey: response.data.authKey || response.data.AuthKey,
        userData: response.data
      };
    } else {
      throw {
        message: response.data.Message || response.data.message || "Login failed"
      };
    }
  } catch (error) {
    console.error("Error with service number login:", error);
    throw error.response?.data || error || { 
      message: "Server Error. Unable to login with service number." 
    };
  }
};

// Get user details by service number
export const getUserDetailsByServiceNo = async (serviceNo) => {
  const url = `${BASE_URL}/UserDetails/GetUserDetailsByUser?sno=${serviceNo}`;
  
  try {
    const response = await axios.get(url);
    console.log("User Details Response:", response.data);
    
    if (response.data?.resultSet && response.data.resultSet.length > 0) {
      return {
        success: true,
        userData: response.data.resultSet[0],
        fullResponse: response.data
      };
    } else {
      return {
        success: false,
        message: "No user details found",
        fullResponse: response.data
      };
    }
  } catch (error) {
    console.error("Error fetching user details:", error);
    throw error.response
      ? error.response.data
      : { message: "Server Error. Unable to fetch user details." };
  }
};

// Get service number by phone number
export const getServiceNumberByPhone = async (phoneNumber) => {
  const cleanNumber = phoneNumber.replace("+", "");
  const url = `${BASE_URL}/UserDetails/GetUserDetailsByPhone?phone=${cleanNumber}`;
  
  try {
    const response = await axios.get(url);
    console.log("Service Number by Phone Response:", response.data);
    
    if (response.data?.resultSet && response.data.resultSet.length > 0) {
      return {
        success: true,
        serviceNo: response.data.resultSet[0].ServiceNo,
        userData: response.data.resultSet[0]
      };
    } else {
      return {
        success: false,
        message: "No service number found for this phone",
        fullResponse: response.data
      };
    }
  } catch (error) {
    console.error("Error fetching service number by phone:", error);
    throw error.response
      ? error.response.data
      : { message: "Server Error. Unable to fetch service number." };
  }
};

// Upload profile image (Fixed for PNG support)
export const uploadProfileImage = async (serviceNo, imageFile) => {
  const url = `${BASE_URL}/User/Fileupload`;
  
  // Create FormData
  const formData = new FormData();
  formData.append("ImageFile", imageFile);
  formData.append("ServiceNo", serviceNo);
  
  // Set proper headers for file upload
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    params: {
      ServiceNo: serviceNo
    }
  };
  
  try {
    console.log("Uploading image for service:", serviceNo);
    console.log("File details:", imageFile.name, imageFile.type, imageFile.size);
    
    const response = await axios.post(url, formData, config);
    console.log("Image upload response:", response.data);
    
    return {
      success: true,
      data: response.data,
      imageUrl: apiUrl(`User/GetUserImg?Sno=${serviceNo}&t=${new Date().getTime()}`)
    };
  } catch (error) {
    console.error("Error uploading profile image:", error);
    
    // Try alternative upload method if first fails
    try {
      console.log("Trying alternative upload method...");
      
      // Alternative: Use fetch API which might handle CORS better
      const formDataAlt = new FormData();
      formDataAlt.append("ImageFile", imageFile);
      formDataAlt.append("ServiceNo", serviceNo);
      
      const fetchResponse = await fetch(apiUrl(`${url}?ServiceNo=${serviceNo}`), {
        method: 'POST',
        body: formDataAlt,
      });
      
      const result = await fetchResponse.text();
      console.log("Alternative upload result:", result);
      
      if (fetchResponse.ok) {
        return {
          success: true,
          data: result,
          imageUrl: apiUrl(`User/GetUserImg?Sno=${serviceNo}&t=${new Date().getTime()}`)
        };
      } else {
        throw new Error(result || "Upload failed");
      }
    } catch (altError) {
      console.error("Alternative upload also failed:", altError);
      throw error.response?.data || altError || { 
        message: "Server Error. Unable to upload image." 
      };
    }
  }
};

// Get user image URL
export const getUserImageUrl = (serviceNo) => {
  if (!serviceNo) return null;
  return apiUrl(`User/GetUserImg?Sno=${serviceNo}&t=${new Date().getTime()}`);
};

// Update user details (for edit profile functionality)
// Uses POST method like Registration form to match backend expectations
export const updateUserDetails = async (userData) => {
  const url = `${BASE_URL}/UserRegister/PostUserDetails`;
  
  try {
    // Send as array like registration does, using POST not PUT
    const response = await axios.post(url, [userData]);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("Error updating user details:", error);
    throw error.response
      ? error.response.data
      : { message: "Server Error. Unable to update user details." };
  }
};

// Change user password
export const changeUserPassword = async (serviceNo, currentPassword, newPassword) => {
  const url = `${BASE_URL}/UserRegister/ChangePassword`;
  
  try {
    const response = await axios.post(url, {
      ServiceNo: serviceNo,
      CurrentPassword: currentPassword,
      NewPassword: newPassword
    });
    
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("Error changing password:", error);
    throw error.response
      ? error.response.data
      : { message: "Server Error. Unable to change password." };
  }
};


















// import axios from "axios";

// // Base URL configuration
// const BASE_URL = "https://daladamaligawatest.dockyardsoftware.com/";

// // Get Max Service Number API
// export const getMaxServiceNumber = async () => {
//   const url = `${BASE_URL}/UserRegister/maxId`;
  
//   try {
//     const response = await axios.get(url);
//     return response.data;
//   } catch (error) {
//     console.error("Error fetching max service number:", error);
//     throw error.response
//       ? error.response.data
//       : { message: "Server Error. Unable to fetch service number." };
//   }
// };

// // Registration API
// export const registerUser = async (userData) => {
//   const url = `${BASE_URL}/UserRegister/PostUserDetails`;
  
//   try {
//     const response = await axios.post(url, [userData]);
//     return response.data;
//   } catch (error) {
//     console.error("Error registering user:", error);
//     throw error.response
//       ? error.response.data
//       : { message: "Server Error. Unable to register user." };
//   }
// };

// // Combined OTP verification and send API (Updated to return service number)
// export const verificationCallWithValid = async (phoneNumber) => {
//   const url = `${BASE_URL}/Login/VerificationCallWithValid`;
  
//   const cleanNumber = phoneNumber.replace("+", "");
  
//   try {
//     const response = await axios.post(url, {
//       MobileNumber: cleanNumber
//     });
    
//     console.log("OTP Verification Response:", response.data);
    
//     // Extract service number from response
//     let serviceNo = null;
//     if (response.data?.serviceNo?.resultSet?.[0]?.serviceNo) {
//       serviceNo = response.data.serviceNo.resultSet[0].serviceNo;
//     } else if (response.data?.serviceNo?.resultSet?.[0]?.serviceno) {
//       serviceNo = response.data.serviceNo.resultSet[0].serviceno;
//     } else if (response.data?.resultSet?.[0]?.serviceNo) {
//       serviceNo = response.data.resultSet[0].serviceNo;
//     }
    
//     // If still no service number, try to fetch it separately
//     if (!serviceNo) {
//       try {
//         // Try to get service number by phone number
//         const userDetailsResponse = await axios.get(
//           `${BASE_URL}/UserDetails/GetUserDetailsByPhone?phone=${cleanNumber}`
//         );
        
//         if (userDetailsResponse.data?.resultSet?.[0]?.ServiceNo) {
//           serviceNo = userDetailsResponse.data.resultSet[0].ServiceNo;
//         }
//       } catch (userError) {
//         console.warn("Could not fetch service number by phone:", userError);
//       }
//     }
    
//     return {
//       success: true,
//       otp: response.data.otp || response.data.VerificationCode,
//       serviceNo: serviceNo,
//       rawResponse: response.data
//     };
//   } catch (error) {
//     console.error("Error sending verification:", error);
//     throw error.response
//       ? error.response.data
//       : { message: "Server Error. Unable to send verification." };
//   }
// };

// // NEW: Service Number Login API
// export const serviceNumberLogin = async (serviceNo, password) => {
//   const url = `${BASE_URL}/Login/UserLogin`;
  
//   try {
//     const response = await axios.post(url, null, {
//       params: {
//         serviceNo: serviceNo,
//         password: password
//       }
//     });
    
//     console.log("Service Login Response:", response.data);
    
//     // Check if login was successful
//     if (response.data.StatusCode === 200) {
//       return {
//         success: true,
//         data: response.data,
//         authKey: response.data.authKey || response.data.AuthKey,
//         userData: response.data
//       };
//     } else {
//       throw {
//         message: response.data.Message || response.data.message || "Login failed"
//       };
//     }
//   } catch (error) {
//     console.error("Error with service number login:", error);
//     throw error.response?.data || error || { 
//       message: "Server Error. Unable to login with service number." 
//     };
//   }
// };

// // Fetch user data by phone number (keeping this for compatibility)
// export const fetchUserData = async (phoneNumber) => {
//   const headers = {
//     UserId: "DTSUSER",
//     Password: "Rayan@123",
//   };

//   const cleanNumber = phoneNumber.replace(/[^0-9+]/g, '');
  
//   let withPlus = cleanNumber;
//   let withoutPlus = cleanNumber;

//   if (cleanNumber.startsWith('94')) {
//     withPlus = `+${cleanNumber}`;
//     withoutPlus = cleanNumber;
//   } else if (cleanNumber.startsWith('+94')) {
//     withPlus = cleanNumber;
//     withoutPlus = cleanNumber.slice(1);
//   }

//   const tryFormat = async (number) => {
//     try {
//       headers.PhoneNo = number;
//       const response = await axios.get(
//         `${BASE_URL}/attme/Login`,
//         { headers }
//       );
      
//       if (response?.data?.responceType === true && response?.data?.responceMessage) {
//         return {
//           success: true,
//           data: response.data,
//           phoneFormat: number
//         };
//       }
//       return { success: false };
//     } catch (error) {
//       console.log(`Failed with format ${number}:`, error);
//       return { success: false };
//     }
//   };

//   try {
//     const plusResult = await tryFormat(withPlus);
//     if (plusResult.success) {
//       return {
//         ...plusResult.data,
//         correctPhoneFormat: withPlus,
//         headers: { ...headers, PhoneNo: withPlus }
//       };
//     }

//     const noPlusResult = await tryFormat(withoutPlus);
//     if (noPlusResult.success) {
//       return {
//         ...noPlusResult.data,
//         correctPhoneFormat: withPlus,
//         headers: { ...headers, PhoneNo: withPlus }
//       };
//     }

//     throw new Error("Neither phone format was accepted");
//   } catch (error) {
//     console.error("Error fetching user data:", error);
//     throw error.response
//       ? error.response.data
//       : { message: "Server Error. Unable to fetch user data." };
//   }
// };

// // NEW: Get user details by service number
// export const getUserDetailsByServiceNo = async (serviceNo) => {
//   const url = `${BASE_URL}/UserDetails/GetUserDetailsByUser?sno=${serviceNo}`;
  
//   try {
//     const response = await axios.get(url);
//     console.log("User Details Response:", response.data);
    
//     if (response.data?.resultSet && response.data.resultSet.length > 0) {
//       return {
//         success: true,
//         userData: response.data.resultSet[0],
//         fullResponse: response.data
//       };
//     } else {
//       return {
//         success: false,
//         message: "No user details found",
//         fullResponse: response.data
//       };
//     }
//   } catch (error) {
//     console.error("Error fetching user details:", error);
//     throw error.response
//       ? error.response.data
//       : { message: "Server Error. Unable to fetch user details." };
//   }
// };

// // NEW: Get service number by phone number
// export const getServiceNumberByPhone = async (phoneNumber) => {
//   const cleanNumber = phoneNumber.replace("+", "");
//   const url = `${BASE_URL}/UserDetails/GetUserDetailsByPhone?phone=${cleanNumber}`;
  
//   try {
//     const response = await axios.get(url);
//     console.log("Service Number by Phone Response:", response.data);
    
//     if (response.data?.resultSet && response.data.resultSet.length > 0) {
//       return {
//         success: true,
//         serviceNo: response.data.resultSet[0].ServiceNo,
//         userData: response.data.resultSet[0]
//       };
//     } else {
//       return {
//         success: false,
//         message: "No service number found for this phone",
//         fullResponse: response.data
//       };
//     }
//   } catch (error) {
//     console.error("Error fetching service number by phone:", error);
//     throw error.response
//       ? error.response.data
//       : { message: "Server Error. Unable to fetch service number." };
//   }
// };






















// import axios from "axios";

// // Registration API
// export const registerUser = async (userData) => {
//   const url = `UserRegister/PostUserDetails`;
  
//   try {
//     const response = await axios.post(url, [userData]);
//     return response.data;
//   } catch (error) {
//     console.error("Error registering user:", error);
//     throw error.response
//       ? error.response.data
//       : { message: "Server Error. Unable to register user." };
//   }
// };

// // Combined OTP verification and send API
// export const verificationCallWithValid = async (phoneNumber) => {
//   const url = `Login/VerificationCallWithValid`;
  
//   // Clean the phone number (remove + if present)
//   const cleanNumber = phoneNumber.replace("+", "");
  
//   try {
//     const response = await axios.post(url, {
//       MobileNumber: cleanNumber
//     });
    
//     return response.data;
//   } catch (error) {
//     console.error("Error sending verification:", error);
//     throw error.response
//       ? error.response.data
//       : { message: "Server Error. Unable to send verification." };
//   }
// };

// // Fetch user data (keeping this for compatibility)
// export const fetchUserData = async (phoneNumber) => {
//   const headers = {
//     UserId: "DTSUSER",
//     Password: "Rayan@123",
//   };

//   // Clean the phone number
//   const cleanNumber = phoneNumber.replace(/[^0-9+]/g, '');
  
//   // Create both formats
//   let withPlus = cleanNumber;
//   let withoutPlus = cleanNumber;

//   // Format the numbers correctly
//   if (cleanNumber.startsWith('94')) {
//     withPlus = `+${cleanNumber}`;
//     withoutPlus = cleanNumber;
//   } else if (cleanNumber.startsWith('+94')) {
//     withPlus = cleanNumber;
//     withoutPlus = cleanNumber.slice(1);
//   }

//   const tryFormat = async (number) => {
//     try {
//       headers.PhoneNo = number;
//       const response = await axios.get(
//         `attme/Login`,
//         { headers }
//       );
      
//       if (response?.data?.responceType === true && response?.data?.responceMessage) {
//         return {
//           success: true,
//           data: response.data,
//           phoneFormat: number
//         };
//       }
//       return { success: false };
//     } catch (error) {
//       console.log(`Failed with format ${number}:`, error);
//       return { success: false };
//     }
//   };

//   try {
//     const plusResult = await tryFormat(withPlus);
//     if (plusResult.success) {
//       return {
//         ...plusResult.data,
//         correctPhoneFormat: withPlus,
//         headers: { ...headers, PhoneNo: withPlus }
//       };
//     }

//     const noPlusResult = await tryFormat(withoutPlus);
//     if (noPlusResult.success) {
//       return {
//         ...noPlusResult.data,
//         correctPhoneFormat: withPlus,
//         headers: { ...headers, PhoneNo: withPlus }
//       };
//     }

//     throw new Error("Neither phone format was accepted");
//   } catch (error) {
//     console.error("Error fetching user data:", error);
//     throw error.response
//       ? error.response.data
//       : { message: "Server Error. Unable to fetch user data." };
//   }
// };







// import axios from "axios";

// // const BASE_URL = "https://esystems.cdl.lk/backend/AttendMe2/attme/";

// export const authenticatePhoneNo = async (phoneNo) => {

//   const url = `Login/authenticatePhone`;
 
//  try {
//     const response = await axios.post(
//       url,
//       {},
//       {
//         headers: {
//           phoneNo: phoneNo, 
//         }
//       }
//     );

//     return response.data;
//   } catch (error) {
//     console.error("Error authenticating phone number:", error);
//     throw error.response
//       ? error.response.data
//       : { message: "Server Error. Unable to authenticate phone number." };
//   }
// };

// export const verificationCall = async (phoneNumber) => {
//   const headers = {
//     UserId: "DTSUSER",
//     Password: "Rayan@123",
//     "Content-Type": "application/json",
//   };

  
//   const plainPhoneNumber = phoneNumber.replace("+", "");

//   const url = `attme/VerificationCall`;
//   const body = { MobileNumber: plainPhoneNumber };

//   try {
//     const response = await axios.post(url, body, { headers });
    
//     return response.data;
//   } catch (error) {
//     console.error("Error sending verification call:", error);
//     throw error.response
//       ? error.response.data
//       : { message: "Server Error. Unable to send verification call." };
//   }
// };



// export const fetchUserData = async (phoneNumber) => {
//   const headers = {
//       UserId: "DTSUSER",
//       Password: "Rayan@123",
//   };

//   // Clean the phone number
//   const cleanNumber = phoneNumber.replace(/[^0-9+]/g, '');
  
//   // Create both formats
//   let withPlus = cleanNumber;
//   let withoutPlus = cleanNumber;

//   // Format the numbers correctly
//   if (cleanNumber.startsWith('94')) {
//       withPlus = `+${cleanNumber}`;
//       withoutPlus = cleanNumber;
//   } else if (cleanNumber.startsWith('+94')) {
//       withPlus = cleanNumber;
//       withoutPlus = cleanNumber.slice(1);
//   }

//   const tryFormat = async (number) => {
//       try {
//           headers.PhoneNo = number;
//           const response = await axios.get(
//               `attme/Login`,
//               { headers }
//           );
          
//           // Check both responceType and responceMessage
//           if (response?.data?.responceType === true && response?.data?.responceMessage) {
//               return {
//                   success: true,
//                   data: response.data,
//                   phoneFormat: number
//               };
//           }
//           return { success: false };
//       } catch (error) {
//           console.log(`Failed with format ${number}:`, error);
//           return { success: false };
//       }
//   };

//   try {
//       // Try both formats sequentially to ensure proper handling
//       const plusResult = await tryFormat(withPlus);
//       if (plusResult.success) {
//           return {
//               ...plusResult.data,
//               correctPhoneFormat: withPlus,
//               headers: { ...headers, PhoneNo: withPlus }
//           };
//       }

//       const noPlusResult = await tryFormat(withoutPlus);
//       if (noPlusResult.success) {
//           return {
//               ...noPlusResult.data,
//               correctPhoneFormat: withPlus, // Always store as +94 format
//               headers: { ...headers, PhoneNo: withPlus }
//           };
//       }

//       throw new Error("Neither phone format was accepted");
//   } catch (error) {
//       console.error("Error fetching user data:", error);
//       throw error.response
//           ? error.response.data
//           : { message: "Server Error. Unable to fetch user data." };
//   }
// };


